import {
  Select,
  AutoComplete,
  Table,
  Button,
  DatePicker,
  Spin,
  Modal,
} from 'antd'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CloseOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom/cjs/react-router-dom'
import moment from 'moment'
import { H3, H4 } from 'src/branding/typography'
import { createXeroBill, fetchXeroProducts } from 'src/features/ReceiptingSlice'
import {
  fetchXeroOrganizationData,
  clearXeroData,
  searchXeroContacts,
} from 'src/features/XeroIntegrationSlice'
import dayjs from 'dayjs'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import Loading from 'src/components/styled/Loading'
import imageCompression from 'browser-image-compression'
import { getInvoiceByUrl } from 'src/features/InvoiceSlice'
import { getPrice, getQuantity } from 'src/utils/priceFunctions'
import {
  resolveXeroOrganization,
  resolveXeroOrganizationId,
} from 'src/utils/xeroOrganization'
import {
  resolveXeroSupplierInvoiceNumber,
  withXeroSupplierInvoiceNumber,
  buildXeroBillLineItems,
  planXeroExportJobs,
  partitionFinanceDocuments,
} from 'src/utils/xeroAutoSendBill'
import { resolveLineVatTreatment } from 'src/utils/vatTreatment'
import AttachedInvoicePreview from 'src/screens/receipting/components/AttachedInvoicePreview'
import { getUploadedInvoices } from 'src/features/NewInvoicesSlice'
import { getLinkedInvoicesForReceiptingOrder } from 'src/utils/reconciliationHealth'
import { enrichReceiptingRowsWithNominalCodes } from 'src/utils/enrichReceiptingRowsWithNominalCodes'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import { collectOrderInvoiceUrls } from 'src/utils/collectOrderInvoiceUrls'

function withFileProxyQuery(url) {
  const raw = String(url || '').trim()
  if (!raw) return raw
  try {
    const parsed = new URL(raw)
    parsed.searchParams.set('proxy', '1')
    return parsed.toString()
  } catch (e) {
    return raw.includes('?') ? `${raw}&proxy=1` : `${raw}?proxy=1`
  }
}

const SendToXero = () => {
  const venue = useSelector(state => state.venue.venue)
  const xero = venue?.xeroConnection
  const [selectedXeroAccount, setSelectedXeroAccount] = useState(null)
  const [selectedXeroContact, setSelectedXeroContact] = useState(null)
  const [filteredAccounts, setFilteredAccounts] = useState([])
  const [filteredAccountOptions, setFilteredAccountOptions] = useState([])
  const location = useLocation()
  const {
    order,
    forceHint,
    invoicePreviewUrls: navPreviewUrls,
  } = location.state || {}
  const xeroProducts = useSelector(state => state.receipting.xeroProducts)
  const venueProducts = useSelector(
    state => state.venueProducts?.products || [],
  )
  const uploadedInvoices = useSelector(
    state => state.newInvoices?.uploadedInvoices || [],
  )
  const xeroIntegration = useSelector(state => state.xeroIntegration) || {
    accounts: [],
    contacts: [],
    accountsLoading: false,
    contactsLoading: false,
    isLoading: false,
    error: null,
  }
  // const receptingOrders = receiptingProducts?.map(item => item.orders)
  const supplier = order?.supplier
  const receptingOrder = xeroProducts
    ?.flat()
    .filter(order => order.supplierConfirmed === true)
  const dispatch = useDispatch()
  const [date, setDate] = useState('')
  const [files, setFiles] = useState([])
  /** Auto-loaded from the GR order — previewed via URL, still attached on send. */
  const [orderAttachmentFiles, setOrderAttachmentFiles] = useState([])
  const fileInputRef = useRef(null)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const isLoading = useSelector(state => state?.receipting?.isLoading)
  const [searchAccount, setSearchAccount] = useState('')
  const [filteredContactOptions, setFilteredContactOptions] = useState([])
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const orderInvoiceLoadAttempted = useRef(false)
  const [contactSearch, setContactSearch] = useState('')
  const [skipNextContactSearch, setSkipNextContactSearch] = useState(false)
  const [isSearchingContacts, setIsSearchingContacts] = useState(false)
  const [forceSend, setForceSend] = useState(Boolean(forceHint))

  const orderPreviewUrls = useMemo(
    () => collectOrderInvoiceUrls(order, navPreviewUrls, uploadedInvoices),
    [order, navPreviewUrls, uploadedInvoices],
  )

  const mappedOrders = useMemo(() => {
    const rows = receptingOrder?.map(item => ({
      ...item,
      price: item?.receipting?.updatedPrice
        ? item?.receipting?.updatedPrice
        : getPrice(item),
      quantity: getQuantity(item),
    }))
    return enrichReceiptingRowsWithNominalCodes(
      rows || [],
      venueProducts,
      order?.supplier?._id || order?.supplier,
    )
  }, [receptingOrder, venueProducts, order?.supplier])

  const filePreviews = useMemo(() => {
    return (files || []).map(f => ({
      file: f,
      url: URL.createObjectURL(f),
      isImage: (f?.type || '').startsWith('image/'),
      isPdf:
        f?.type === 'application/pdf' ||
        (f?.name || '').toLowerCase().endsWith('.pdf'),
    }))
  }, [files])

  useEffect(() => {
    return () => {
      filePreviews.forEach(p => {
        try {
          URL.revokeObjectURL(p.url)
        } catch (e) {
          // ignore
        }
      })
    }
  }, [filePreviews])

  console.log('mappedOrders', mappedOrders)
  console.log('order', order)
  console.log('xeroProducts', receptingOrder)

  useEffect(() => {
    dispatch(
      fetchXeroProducts(
        venue?._id,
        order?.supplier?._id,
        order?.purchaseOrderNumber,
      ),
    )
    if (venue?._id) {
      dispatch(fetchVenueProducts(venue._id))
    }
  }, [order, venue?._id, dispatch])

  // Default to resolved destination org (defaultOrganizationId), not organizations[0]
  useEffect(() => {
    if (!xero?.organizations?.length || selectedOrganization) return
    const resolved = resolveXeroOrganization(xero)
    if (resolved) setSelectedOrganization(resolved)
  }, [xero, selectedOrganization])

  useEffect(() => {
    if (venue?._id && (!uploadedInvoices || uploadedInvoices.length === 0)) {
      dispatch(getUploadedInvoices(venue._id))
    }
  }, [venue?._id, uploadedInvoices?.length, dispatch])

  useEffect(() => {
    const isPdfFile = file =>
      !!file &&
      (file.type === 'application/pdf' ||
        (file.name || '').toLowerCase().endsWith('.pdf'))

    const compressToFile = async srcFile => {
      // Only images can be compressed / previewed as <img>.
      // PDFs should be kept as-is so they can be attached to Xero.
      if (
        !srcFile ||
        isPdfFile(srcFile) ||
        !(srcFile.type || '').startsWith('image/')
      ) {
        return srcFile
      }
      const options = {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      }
      const compressedBlob = await imageCompression(srcFile, options)
      return new File([compressedBlob], srcFile.name || 'invoice.jpg', {
        type: compressedBlob.type,
      })
    }

    const fetchUrlAsFile = async (url, fallbackName = 'invoice-from-url') => {
      // Stream via Express (?proxy=1) — signed S3 redirects break under CORS.
      const response = await fetch(withFileProxyQuery(url))
      if (!response.ok) {
        throw new Error(`Failed to download invoice (${response.status})`)
      }
      const blob = await response.blob()
      const urlTail = decodeURIComponent(
        String(url).split('?')[0].split('/').pop() || fallbackName,
      )
      const baseName = urlTail || fallbackName
      const looksLikePdf =
        blob.type === 'application/pdf' ||
        baseName.toLowerCase().endsWith('.pdf')
      const name =
        looksLikePdf && !baseName.toLowerCase().endsWith('.pdf')
          ? `${baseName}.pdf`
          : baseName
      return new File([blob], name, {
        type: blob.type || (looksLikePdf ? 'application/pdf' : blob.type),
      })
    }

    const loadInvoicesFromOrder = async urls => {
      // Prefer modern attach id so createXeroBill can update invoice status.
      const detailId = order?.attachedInvoiceDetails?.[0]?.id
      const linked = getLinkedInvoicesForReceiptingOrder(
        order,
        uploadedInvoices,
      )
      if (detailId) {
        setSelectedInvoice(prev => prev || { _id: detailId, id: detailId })
      } else if (linked[0]?._id || linked[0]?.id) {
        const id = linked[0]._id || linked[0].id
        setSelectedInvoice(prev => prev || { ...linked[0], _id: id, id })
      }

      try {
        const incoming = []

        for (const url of urls) {
          try {
            const f = await fetchUrlAsFile(url)
            const cf = await compressToFile(f)
            if (cf) incoming.push(cf)
          } catch (e) {
            console.warn('Failed to load invoice file for Xero attach:', url, e)
          }
        }

        // Resolve library invoice metadata from the first URL when needed.
        if (urls[0] && !detailId && !linked[0]) {
          try {
            const selectedInvoiceData = await dispatch(getInvoiceByUrl(urls[0]))
            if (selectedInvoiceData) setSelectedInvoice(selectedInvoiceData)
          } catch (e) {
            // non-fatal
          }
        }

        if (incoming.length > 0) {
          setOrderAttachmentFiles(incoming)
        }
      } catch (error) {
        console.error('Failed to load invoice files from order:', error)
      } finally {
        setInvoiceLoading(false)
      }
    }

    const urls = collectOrderInvoiceUrls(
      order,
      navPreviewUrls,
      uploadedInvoices,
    )
    // Wait for invoices library when the order itself has no file URLs yet.
    if (
      urls.length === 0 &&
      order?.purchaseOrderNumber &&
      (!uploadedInvoices || uploadedInvoices.length === 0)
    ) {
      return
    }
    if (urls.length === 0) return
    if (orderInvoiceLoadAttempted.current) return

    orderInvoiceLoadAttempted.current = true
    setInvoiceLoading(true)
    loadInvoicesFromOrder(urls)
  }, [order, navPreviewUrls, uploadedInvoices, dispatch])

  const onChangeDate = dateString => {
    if (dateString) {
      setDate(dateString)
    } else {
      console.log('error')
    }
  }

  useEffect(() => {
    if (selectedOrganization) {
      // Use Redux state for accounts and contacts
      setFilteredAccountOptions(xeroIntegration?.accounts || [])
      setFilteredContactOptions(xeroIntegration?.contacts || [])
      setFilteredAccounts(xeroIntegration?.accounts || [])
    }
  }, [
    selectedOrganization,
    xeroIntegration?.accounts,
    xeroIntegration?.contacts,
    xero?.organizations,
  ])

  useEffect(() => {
    if (
      order?.venueSupplier.defaultAccount &&
      order?.venueSupplier.defaultContact
    ) {
      const selectedAccount = filteredAccountOptions.find(
        account => account.AccountID === order?.venueSupplier.defaultAccount,
      )
      setSelectedXeroAccount(selectedAccount)
      // Prefer saved contact details — we no longer bulk-load 300 contacts.
      const selectedContact =
        order?.venueSupplier?.defaultContactDetails ||
        filteredContactOptions.find(
          contact => contact.ContactID === order?.venueSupplier.defaultContact,
        )
      setSelectedXeroContact(selectedContact)
    }
  }, [order.venueSupplier, filteredAccountOptions, filteredContactOptions])

  // Fetch organization data when organization is selected
  useEffect(() => {
    if (selectedOrganization?.id) {
      dispatch(fetchXeroOrganizationData(selectedOrganization.id, venue?._id))
    }
  }, [selectedOrganization?.id, dispatch, venue?._id])

  // Debounced contact search against API
  useEffect(() => {
    // If we just selected a contact, skip one effect run
    if (skipNextContactSearch) {
      setSkipNextContactSearch(false)
      return
    }

    // If input is empty, don't search
    if (!contactSearch.trim()) {
      return
    }

    // Need venue and organization to search
    if (!venue?._id || !selectedOrganization?.id) {
      return
    }

    setIsSearchingContacts(true)
    const handler = setTimeout(async () => {
      try {
        await dispatch(
          searchXeroContacts(
            selectedOrganization.id,
            venue._id,
            contactSearch.trim(),
          ),
        )
      } catch (error) {
        console.error('Contact search error:', error)
      } finally {
        setIsSearchingContacts(false)
      }
    }, 600) // call API after 600ms of inactivity

    return () => {
      clearTimeout(handler)
    }
  }, [
    contactSearch,
    dispatch,
    selectedOrganization?.id,
    venue?._id,
    skipNextContactSearch,
  ])

  // Cleanup Redux state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearXeroData())
    }
  }, [dispatch])

  const handleFileChange = async e => {
    const selectedList = Array.from(e.target.files || [])
    if (!selectedList.length) return

    try {
      const options = {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      }

      const processed = []
      for (const selectedFile of selectedList) {
        const isPdf =
          selectedFile.type === 'application/pdf' ||
          (selectedFile.name || '').toLowerCase().endsWith('.pdf')

        if (isPdf || !(selectedFile.type || '').startsWith('image/')) {
          // Keep PDFs (and any non-image) as-is.
          if (selectedFile.size <= 3 * 1024 * 1024) processed.push(selectedFile)
          continue
        }

        const compressedBlob = await imageCompression(selectedFile, options)
        const compressedFile = new File(
          [compressedBlob],
          selectedFile.name || 'invoice.jpg',
          { type: compressedBlob.type },
        )
        if (compressedFile.size <= 3 * 1024 * 1024)
          processed.push(compressedFile)
      }

      if (processed.length === 0) {
        RejectedNotification({
          message: 'File(s) too large',
          description: 'After compression, selected file(s) exceed 3MB.',
        })
        return
      }

      setFiles(prev => {
        const names = new Set(prev.map(f => f.name))
        const unique = processed.filter(f => !names.has(f.name))
        return [...prev, ...unique]
      })
    } catch (error) {
      console.error('Error compressing file(s):', error)
      RejectedNotification({
        message: 'File Error',
        description: 'Could not compress one or more files.',
      })
    } finally {
      // reset input to allow reselection of same file names
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async ({ forceSendOverride = false } = {}) => {
    if (!selectedXeroAccount || !date) {
      RejectedNotification({
        message: 'Submission Failed',
        description: 'Account and due date are required',
      })
      return
    }

    const supplierInvoiceNumber = resolveXeroSupplierInvoiceNumber({
      order,
      invoice: selectedInvoice,
      invoiceNumber:
        selectedInvoice?.invoiceNumber ||
        order?.invoiceNumber ||
        order?.attachedInvoiceDetails?.[0]?.invoiceNumber ||
        order?.attachedInvoiceNumber,
    })
    if (!supplierInvoiceNumber) {
      RejectedNotification({
        message: 'Supplier invoice number required',
        description:
          'This order has no supplier invoice number in OMS. Attach/enter the supplier invoice number before sending to Xero — otherwise Xero will not show it on the bill.',
      })
      return
    }

    const useForce = forceSendOverride || forceSend
    const orgName =
      selectedOrganization?.name ||
      resolveXeroOrganization(xero)?.name ||
      'Xero'

    const linkedDocsForConfirm = getLinkedInvoicesForReceiptingOrder(
      order,
      uploadedInvoices,
    )
    const { invoices: confirmInvoices, creditNotes: confirmCredits } =
      partitionFinanceDocuments(linkedDocsForConfirm)
    const willExportDual =
      confirmInvoices.length > 0 && confirmCredits.length > 0

    Modal.confirm({
      title: willExportDual
        ? 'Create AUTHORISED bill and credit note in Xero?'
        : 'Create AUTHORISED bill in Xero?',
      content: willExportDual
        ? `This creates a separate AUTHORISED bill and credit note in ${orgName} using each OMS document's face value and reference (not a netted single bill).${
            useForce
              ? ' Force send will create the contact as Supplier (OMS).'
              : ''
          }`
        : `This creates an AUTHORISED ${
            order?.totalSpendReceived < 0 ? 'credit note' : 'bill'
          } in ${orgName} with supplier invoice # ${supplierInvoiceNumber}.${
            useForce
              ? ' Force send will create the contact as Supplier (OMS).'
              : ''
          }`,
      okText: useForce ? 'Force send' : 'Send to Xero',
      onOk: () => submitBill({ forceSend: useForce }),
    })
  }

  const submitBill = ({ forceSend: forceSendFlag = false } = {}) => {
    const isCreditNote = order?.totalSpendReceived < 0
    const linkedDocs = getLinkedInvoicesForReceiptingOrder(
      order,
      uploadedInvoices,
    )
    const financeDocuments = linkedDocs.length
      ? linkedDocs
      : selectedInvoice
      ? [selectedInvoice]
      : []

    let plan
    try {
      plan = planXeroExportJobs({
        financeDocuments,
        venue,
        order,
        venueSupplier: order?.venueSupplier,
        receiptingProducts: mappedOrders || [],
        forceSend: forceSendFlag,
        xeroAccounts: xeroIntegration?.accounts || [],
        xeroTaxRates: xeroIntegration?.taxRates || [],
        supplierDefaultAccountId: selectedXeroAccount?.AccountID,
        selectedContactId: selectedXeroContact?.ContactID,
        invoice: selectedInvoice,
        dueDate: date,
      })
    } catch (accountError) {
      RejectedNotification({
        message: 'Cannot send to Xero',
        description: accountError?.message || 'Account mapping failed',
      })
      return
    }

    // When dual document export is planned, prefer that path (Bill + CN).
    // Otherwise fall back to the existing single-bill builder with UI contact.
    if (plan.mode === 'documents') {
      if (plan.jobs.length === 0) {
        SuccessNotification({
          message: 'Already in Xero',
          description:
            'Invoice and credit note for this order are already in Xero.',
        })
        return
      }
      ;(async () => {
        try {
          let latestBillXeroId = null
          for (const job of plan.jobs) {
            // Preserve force-send contact override on document-face jobs.
            if (forceSendFlag && job.billData) {
              job.billData.Contact = {
                Name: `${supplier?.title || 'Supplier'} (OMS)`,
              }
            } else if (
              selectedXeroContact?.ContactID &&
              job.billData &&
              !forceSendFlag
            ) {
              job.billData.Contact = {
                ContactID: selectedXeroContact.ContactID,
              }
            }
            if (
              job.documentType === 'creditNote' &&
              job.billData &&
              latestBillXeroId &&
              !job.billData.allocateToXeroInvoiceId
            ) {
              job.billData.allocateToXeroInvoiceId = latestBillXeroId
            }
            const result = await dispatch(
              createXeroBill(
                job.billData,
                [...orderAttachmentFiles, ...files],
                venue?._id,
                selectedOrganization?.id ||
                  resolveXeroOrganizationId(xero) ||
                  xero?.organizations?.[0]?.id,
                order?.orderIds,
                job.selectedInvoiceId || selectedInvoice?._id || '',
                { forceSend: forceSendFlag },
              ),
            )
            if (job.documentType === 'invoice' && result?.xeroInvoiceId) {
              latestBillXeroId = result.xeroInvoiceId
            }
          }
        } catch (err) {
          RejectedNotification({
            message: 'Xero export failed',
            description: err?.message || 'Could not create all Xero documents',
          })
        }
      })()
      return
    }

    let Lineitems
    try {
      Lineitems = buildXeroBillLineItems({
        rows: mappedOrders || [],
        venueSupplier: order?.venueSupplier,
        venue,
        xeroAccounts: xeroIntegration?.accounts || [],
        xeroTaxRates: xeroIntegration?.taxRates || [],
        forceSend: forceSendFlag,
        supplierDefaultAccountId: selectedXeroAccount?.AccountID,
        isCreditNote,
      }).map(line => ({
        ...line,
        TaxType: selectedXeroAccount?.TaxType === 'OUTPUT2' ? '' : line.TaxType,
      }))
    } catch (accountError) {
      RejectedNotification({
        message: 'Cannot send to Xero',
        description: accountError?.message || 'Account mapping failed',
      })
      return
    }
    const Contact = forceSendFlag
      ? { Name: `${supplier?.title || 'Supplier'} (OMS)` }
      : selectedXeroContact
      ? {
          ContactID: selectedXeroContact.ContactID,
        }
      : { Name: supplier?.title }
    const supplierInvoiceNumber = resolveXeroSupplierInvoiceNumber({
      order,
      invoice: selectedInvoice,
      invoiceNumber:
        selectedInvoice?.invoiceNumber ||
        order?.invoiceNumber ||
        order?.attachedInvoiceDetails?.[0]?.invoiceNumber ||
        order?.attachedInvoiceNumber,
    })
    const billData = withXeroSupplierInvoiceNumber(
      {
        Type: isCreditNote ? 'ACCPAYCREDIT' : 'ACCPAY',
        Contact,
        Date: dayjs(order?.orderedDate).format('YYYY-MM-DD'),
        DueDate: dayjs(date).format('YYYY-MM-DD'),
        LineAmountTypes: 'Exclusive',
        LineItems: Lineitems,
        Status: 'AUTHORISED',
      },
      supplierInvoiceNumber,
      {
        purchaseOrderNumber: order?.purchaseOrderNumber,
        venueTitle: venue?.title,
      },
    )
    const orderIds = order?.orderIds
    const organizationId =
      selectedOrganization?.id ||
      resolveXeroOrganizationId(xero) ||
      xero?.organizations?.[0]?.id

    dispatch(
      createXeroBill(
        billData,
        [...orderAttachmentFiles, ...files],
        venue?._id,
        organizationId,
        orderIds,
        selectedInvoice?._id || selectedInvoice?.id || '',
        { forceSend: forceSendFlag },
      ),
    )
  }

  const columns = [
    {
      title: 'Title',
      dataIndex: 'product.title',
      key: 'title',
      render: text => <H4>{text}</H4>,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: quantity => <b>{quantity}</b>,
    },
    {
      title: 'Unit Price',
      dataIndex: 'price',
      key: 'price',
      render: price => <span>£{price?.toFixed(2)}</span>,
    },
    {
      title: 'Size',
      dataIndex: 'product.size',
      key: 'size',
    },
    {
      title: 'VAT',
      dataIndex: 'product.hasVat',
      key: 'hasVat',
      render: (_, record) => {
        const treatment = resolveLineVatTreatment(record)
        const labels = {
          standard: 'Standard',
          zeroRated: 'Zero-rated',
          noVat: 'No VAT',
        }
        return <span>{labels[treatment] || treatment}</span>
      },
    },
    {
      title: 'Supplier Confirmed',
      dataIndex: 'supplierConfirmed',
      key: 'supplierConfirmed',
      render: confirmed => <span>{confirmed ? 'Yes' : 'No'}</span>,
    },
  ]

  return (
    <ModalView>
      <StyledCard>
        <H3>Send to Xero</H3>
        {xero?.organizations?.length > 1 ? (
          <>
            <Select
              style={{
                width: '100%',
                padding: 10,
                margin: 2,
              }}
              placeholder='Select a Xero organisation'
              value={selectedOrganization?.id}
              onChange={organizationId => {
                if (organizationId) {
                  const org = xero?.organizations?.find(
                    o => o.id === organizationId,
                  )
                  setSelectedOrganization(org || null)
                } else {
                  const defaultOrg = xero?.organizations?.[0]
                  setSelectedOrganization(defaultOrg)
                }
              }}
            >
              {xero?.organizations.map(organization => (
                <Select.Option value={organization.id} key={organization.id}>
                  {organization?.name}
                </Select.Option>
              ))}
            </Select>
            {selectedOrganization && (
              <>
                <AutoComplete
                  style={{
                    width: '100%',
                    padding: 10,
                    margin: 2,
                  }}
                  value={searchAccount || selectedXeroAccount?.Name || ''}
                  placeholder={
                    xeroIntegration?.accountsLoading
                      ? 'Loading accounts...'
                      : 'Select an account'
                  }
                  disabled={xeroIntegration?.accountsLoading}
                  onSelect={value => {
                    const selectedAccount = filteredAccountOptions.find(
                      account => account.AccountID === value,
                    )
                    setSelectedXeroAccount(selectedAccount)
                    setSearchAccount('')
                  }}
                  onSearch={value => {
                    setSelectedXeroAccount('')
                    setSearchAccount(value)
                    const selectedAccounts = filteredAccountOptions.filter(
                      account => {
                        const title = account?.Name || ''
                        return title.toLowerCase().includes(value.toLowerCase())
                      },
                    )
                    setFilteredAccounts(selectedAccounts)
                  }}
                >
                  {filteredAccounts?.map(account => (
                    <AutoComplete.Option
                      value={account?.AccountID}
                      key={account?.AccountID}
                    >
                      {account?.Name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>

                {selectedXeroAccount?.TaxType === 'OUTPUT2' &&
                  selectedXeroAccount?.Type != 'REVENUE' && (
                    <WarningBanner>
                      ⚠️ Heads up! This Xero account is set to{' '}
                      <strong>VAT on Income</strong>,so we cant send VAT info
                      through. To include VAT data, youll need to change the tax
                      settings in Xero.
                    </WarningBanner>
                  )}

                {selectedXeroAccount?.Type === 'REVENUE' && (
                  <WarningBanner>
                    ⚠️ Heads up! The selected Xero account is of type{' '}
                    <strong>Revenue</strong>. You can’t create a bill using a
                    Revenue account in Xero. Please either select a different
                    account or change this account’s type in Xero (for example,
                    to an Expense account) before continuing.
                  </WarningBanner>
                )}
                <AutoComplete
                  style={{
                    width: '100%',
                    padding: 10,
                    margin: 2,
                  }}
                  value={contactSearch || selectedXeroContact?.Name || ''}
                  onSelect={contactId => {
                    const contact = xeroIntegration.contacts.find(
                      c => c.ContactID === contactId,
                    )
                    setSelectedXeroContact(contact)
                    setContactSearch(contact?.Name || '')
                    setSkipNextContactSearch(true)
                  }}
                  onSearch={value => {
                    setSelectedXeroContact(null)
                    setContactSearch(value)
                  }}
                  placeholder='Search contact...'
                  loading={isSearchingContacts}
                  notFoundContent={
                    isSearchingContacts ? (
                      <Spin size='small' />
                    ) : (
                      'Contacts not found'
                    )
                  }
                >
                  {xeroIntegration.contacts.map(contact => (
                    <AutoComplete.Option
                      key={contact.ContactID}
                      value={contact.ContactID}
                    >
                      {contact.Name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </>
            )}
          </>
        ) : (
          <>
            <H4 style={{ marginLeft: 5 }}>
              Xero organisation:{' '}
              {selectedOrganization?.name ||
                resolveXeroOrganization(xero)?.name ||
                xero?.organizations?.[0]?.name}
            </H4>

            <AutoComplete
              style={{
                width: '100%',
                padding: 10,
                margin: 2,
              }}
              value={searchAccount || selectedXeroAccount?.Name || ''}
              placeholder={
                xeroIntegration?.accountsLoading
                  ? 'Loading accounts...'
                  : 'Select an account'
              }
              disabled={xeroIntegration?.accountsLoading}
              onSelect={value => {
                const selectedAccount = filteredAccountOptions?.find(
                  account => account.AccountID === value,
                )
                setSelectedXeroAccount(selectedAccount)
                setSearchAccount('')
              }}
              onSearch={value => {
                setSelectedXeroAccount('')
                setSearchAccount(value)
                const selectedAccounts = filteredAccountOptions?.filter(
                  account => {
                    const title = account?.Name || ''
                    return title.toLowerCase().includes(value.toLowerCase())
                  },
                )
                setFilteredAccounts(selectedAccounts)
              }}
            >
              {filteredAccounts?.map(account => (
                <AutoComplete.Option
                  value={account?.AccountID}
                  key={account?.AccountID}
                >
                  {account?.Name}
                </AutoComplete.Option>
              ))}
            </AutoComplete>

            {selectedXeroAccount?.TaxType === 'OUTPUT2' &&
              selectedXeroAccount?.Type != 'REVENUE' && (
                <WarningBanner>
                  ⚠️ Heads up! This Xero account is set to{' '}
                  <strong>VAT on Income</strong>, so we cant send VAT info
                  through. To include VAT data, youll need to change the tax
                  settings in Xero.
                </WarningBanner>
              )}
            {selectedXeroAccount?.Type === 'REVENUE' && (
              <WarningBanner>
                ⚠️ Heads up! The selected Xero account is of type{' '}
                <strong>Revenue</strong>. You can’t create a bill using a
                Revenue account in Xero. Please either select a different
                account or change this account’s type in Xero (for example, to
                an Expense account) before continuing.
              </WarningBanner>
            )}
            <AutoComplete
              style={{
                width: '100%',
                padding: 10,
                margin: 2,
              }}
              value={contactSearch || selectedXeroContact?.Name || ''}
              onSelect={contactId => {
                const contact = xeroIntegration.contacts.find(
                  c => c.ContactID === contactId,
                )
                setSelectedXeroContact(contact)
                setContactSearch(contact?.Name || '')
                setSkipNextContactSearch(true)
              }}
              onSearch={value => {
                setSelectedXeroContact(null)
                setContactSearch(value)
              }}
              placeholder='Search contact...'
              loading={isSearchingContacts}
              notFoundContent={
                isSearchingContacts ? (
                  <Spin size='small' />
                ) : (
                  'Contacts not found'
                )
              }
            >
              {xeroIntegration?.contacts?.map(contact => (
                <AutoComplete.Option
                  key={contact.ContactID}
                  value={contact.ContactID}
                >
                  {contact.Name}
                </AutoComplete.Option>
              ))}
            </AutoComplete>
          </>
        )}

        <OrderDetails>
          <H4>PO Number: {order?.purchaseOrderNumber}</H4>
          <H4>Order Date: {moment(order?.orderedDate).format('YYYY-MM-DD')}</H4>
          <H4>Order Total: £{order?.totalSpendReceived / 100}</H4>
        </OrderDetails>

        <StyledDatePicker
          placeholder='Select Due Date'
          onChange={onChangeDate}
        />

        <FileUpload>
          <div style={{ width: '100%' }}>
            {orderPreviewUrls.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                {orderPreviewUrls.map((url, idx) => (
                  <AttachedInvoicePreview
                    key={`${url}-${idx}`}
                    url={url}
                    alt={`Attached invoice ${idx + 1}`}
                  />
                ))}
              </div>
            ) : !invoiceLoading ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: 12,
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  color: '#595959',
                }}
              >
                No attached invoice file was found for this PO. You can still
                upload one below before sending to Xero.
              </div>
            ) : null}
            {invoiceLoading ? (
              <div style={{ marginBottom: 12 }}>
                <Loading />
              </div>
            ) : null}
            {files && files.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                {filePreviews.map((p, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    {p.isImage ? (
                      <img src={p.url} alt={`invoice-${idx}`} />
                    ) : p.isPdf ? (
                      <PdfPreview
                        title={p.file?.name || `invoice-${idx}`}
                        src={p.url}
                      />
                    ) : (
                      <FileTile>
                        <div style={{ fontWeight: 600 }}>File</div>
                        <div
                          style={{
                            fontSize: 12,
                            wordBreak: 'break-word',
                            color: '#595959',
                          }}
                        >
                          {p.file?.name || 'invoice'}
                        </div>
                      </FileTile>
                    )}
                    <CloseOutlined
                      onClick={() =>
                        setFiles(prev => prev.filter((_, i) => i !== idx))
                      }
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        fontSize: '16px',
                        background: '#fff',
                        borderRadius: '50%',
                        padding: 2,
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : null}
            <Button onClick={() => fileInputRef.current.click()}>
              {files.length > 0 || orderPreviewUrls.length > 0
                ? 'Add More Invoices'
                : 'Add Invoice(s) (Optional)'}
            </Button>
          </div>
          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept='image/*,application/pdf'
            multiple
            onChange={handleFileChange}
          />
        </FileUpload>

        <Table columns={columns} dataSource={mappedOrders} bordered />

        {(forceHint ||
          order?.attachedInvoiceDetails?.some(
            d => d?.status === 'errorSendingToXero',
          )) && (
          <WarningBanner style={{ marginTop: 12 }}>
            Previous send failed or Force send was requested. You can retry
            normally, or Force send to create the contact as Supplier (OMS).
          </WarningBanner>
        )}

        <Button
          loading={isLoading}
          style={{
            background: '#b5d922',
            color: '#fff',
            width: '100%',
            marginTop: 8,
          }}
          onClick={() => handleSubmit({ forceSendOverride: false })}
        >
          Send to Xero
        </Button>
        {(forceSend ||
          forceHint ||
          order?.attachedInvoiceDetails?.some(
            d => d?.status === 'errorSendingToXero',
          )) && (
          <Button
            loading={isLoading}
            style={{
              width: '100%',
              marginTop: 8,
            }}
            onClick={() => {
              setForceSend(true)
              handleSubmit({ forceSendOverride: true })
            }}
          >
            Force send
          </Button>
        )}
      </StyledCard>
    </ModalView>
  )
}

export default SendToXero

const ModalView = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  background: #fff;
  border-radius: 10px;
`

const StyledCard = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 30px;
  border-radius: 10px;
  text-align: left;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  margin: 20px 0;
  padding: 10px;
`

const FileUpload = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 20px 0;

  img {
    max-width: 700px;
    height: 300px;
    border: 1px solid #ddd;
    border-radius: 5px;
  }

  button {
    background: #b5d922;
    color: #fff;
    width: 100%;
  }
`

const FileTile = styled.div`
  width: 260px;
  height: 120px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fafafa;
`

const PdfPreview = styled.iframe`
  width: 700px;
  height: 300px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background: #fff;
`

const OrderDetails = styled.div`
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  background: #fafafa;
`

const WarningBanner = styled.div`
  background-color: #fffbe6;
  color: #ad8b00;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 12px;
  margin: 8px 0;
  font-size: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`
